import React, { useState } from "react";
import { Spinner, Alert, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PaymentMethodAccordion from "./PaymentMethodAccordion";
import usePaymentMethods from "hooks/userProfile/usePaymentMethods";
import SubtleBadge from "components/common/SubtleBadge";

export default function PaymentInfo({ userId }) {
  const { outgoingMethods, incomingAccounts, connectAccount, loading } = usePaymentMethods(userId);
  const [openIndex, setOpenIndex] = useState(null);
  const handleToggle = (eventKey) => {
    setOpenIndex((prev) => (prev === eventKey ? null : eventKey));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="success" />
        <span className="ms-3 text-sm text-muted">Loading financial profile...</span>
      </div>
    );
  }

  return (
    <div className="payment-info-wrapper">
      {/* SECTION HEADER */}
      <div className="mb-4">
        <h5 className="mb-1 fw-bold">Financial Details</h5>
        <p className="text-muted text-xs mb-0">
          Comprehensive view of the user’s <strong>billing sources</strong> (Outgoing) and <strong>earning destinations</strong> (Incoming).
        </p>
      </div>

      {/* 1. STRIPE CONNECT ACCOUNT STATUS */}
      <div className="mb-4 p-3 border rounded bg-white custom-shadow">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-bold text-xxs text-uppercase text-muted mb-0">Stripe Connect Status</h6>
          {connectAccount && (
             <SubtleBadge bg="secondary">ID: {connectAccount.id}</SubtleBadge>
          )}
        </div>
        
        {connectAccount ? (
          <div className="d-flex flex-wrap gap-2">
            <SubtleBadge bg={(connectAccount.payoutsEnabled ?? connectAccount.payouts_enabled) ? "success" : "danger"}>
              Payouts: {(connectAccount.payoutsEnabled ?? connectAccount.payouts_enabled) ? "ENABLED" : "DISABLED"}
            </SubtleBadge>
            <SubtleBadge bg={(connectAccount.detailsSubmitted ?? connectAccount.details_submitted) ? "success" : "warning"}>
              KYC: {(connectAccount.detailsSubmitted ?? connectAccount.details_submitted) ? "COMPLETED" : "INCOMPLETE"}
            </SubtleBadge>
            <SubtleBadge bg="info">Type: {connectAccount.type?.toUpperCase()}</SubtleBadge>
          </div>
        ) : (
          <Alert variant="light" className="py-2 px-3 mb-0 text-xxs border-dashed">
            <FontAwesomeIcon icon="info-circle" className="me-2 text-info" />
            No Stripe Connect account linked to this user profile.
          </Alert>
        )}
      </div>

      <Row>
        {/* 2. OUTGOING METHODS (REQUESTER) */}
        <Col lg={12} className="mb-4">
          <h6 className="mb-2 fw-bold text-sm text-primary">Funding Sources (Outgoing)</h6>
          {outgoingMethods.length > 0 ? (
            outgoingMethods.map((method, index) => (
              <PaymentMethodAccordion
                key={`out-${method.id || index}`}
                method={{ ...method, direction: 'outgoing' }}
                index={`out-${index}`}
                activeKey={openIndex}
                onToggle={handleToggle}
              />
            ))
          ) : (
            <div className="text-center py-3 border rounded border-dashed text-muted text-xxs bg-light">
              No outgoing payment methods found.
            </div>
          )}
        </Col>

        {/* 3. INCOMING ACCOUNTS (HELPER) */}
        <Col lg={12}>
          <h6 className="mb-2 fw-bold text-sm text-success">Payout Accounts (Incoming)</h6>
          {incomingAccounts.length > 0 ? (
            incomingAccounts.map((method, index) => (
              <PaymentMethodAccordion
                key={`in-${method.id || index}`}
                method={{ ...method, direction: 'incoming' }}
                index={`in-${index}`}
                activeKey={openIndex}
                onToggle={handleToggle}
              />
            ))
          ) : (
            <div className="text-center py-3 border rounded border-dashed text-muted text-xxs bg-light">
              No payout destinations (bank accounts) found.
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}
